<template lang="html">
  <div class="date-picker has-value">
    <input-wrapper
      :error="error"
      :helperText="helperText"
      sneaky="true"
      :disabled="disabled"
      :class="classList">

      <sprite class="input-icon" name="date_range"></sprite>

      <input-label :isRequired="isRequired" :for-id="id" class="pickdate-lable">{{placeholder}}</input-label>

      <div class="selects">
        <styled-select
          v-if="!yearOnly"
          v-model="day"
          placeholder="DD"
          :disabled="disabled"
          class="small">
          <option v-for="day in availableDays">{{day}}</option>
        </styled-select>

        <div class="divider" v-if="!yearOnly"></div>

        <styled-select
          v-if="!yearOnly"
          v-model="month"
          placeholder="MM"
          :disabled="disabled"
          class="small">
          <option v-for="month in availableMonths">{{month}}</option>
        </styled-select>

        <div class="divider" v-if="!yearOnly"></div>

        <styled-select
          v-model="year"
          placeholder="YYYY"
          :disabled="disabled"
          class="big">
          <option v-for="year in availableYears">{{year}}</option>
        </styled-select>
      </div>
    </input-wrapper>
  </div>
</template>

<script>
  import InputLabel from '../../../components/ui/inputs/InputLabel'
  import InputWrapper from '../../../components/ui/inputs/InputWrapper'
  import Sprite from '../../../components/ui/Sprite'
  import StyledSelect from '../../../components/ui/inputs/StyledSelect'

  import input from '../../../mixins/input'

  const MIN_DAYS = 0
  const PAD_WITH = 0
  const DAY_OFFSET = 2
  const MONTH_OFFSET = 1
  const YEAR_OFFSET = 0
  const TOTAL_MONTHS = 12
  const MAX_DAYS = 31
  const FIRST_DAY = 0
  const DAY_MODIFIER = 1
  const PAD_LENGTH = 2

  /**
   * pad a number with leading zeros to make it a required length
   *
   * @param {number} number - number to pad
   * @param {number} digits - length of ouput string
   * @returns {string} `number` padded with leading zeros to length `didgets`
   */
  function padDidgets(number, digits) {
    return Array(Math.max(digits - String(number).length + DAY_MODIFIER, MIN_DAYS))
      .join(PAD_WITH) + number
  }

  export default {
    name: 'DatePicker',

    mixins: [input],

    props: {
      initialDay: {
        required: false,
        type: String
      },

      initialMonth: {
        required: false,
        type: String
      },

      initialYear: {
        required: false,
        type: String
      },

      yearOnly: {
        required: false,
        type: Boolean
      },

      yearFromOffset: {
        default: -10,
        type: Number
      },

      yearToOffset: {
        default: 0,
        type: Number
      },
      disabled: {
        type: Boolean,
        default: false
      },
      isRequired: {
        type: Boolean,
        default: false
      }
    },

    components: {
      InputLabel,
      InputWrapper,
      Sprite,
      StyledSelect
    },

    data() {
      return {
        active: false,
        currentYear: (new Date()).getFullYear(),
        localValue: ''
      }
    },

    mounted() {
      if (this.initialDay) {
        this.day = this.initialDay
      }

      if (this.initialMonth) {
        this.month = this.initialMonth
      }

      if (this.initialYear) {
        this.year = this.initialYear
      }
    },

    watch: {
      value(newVal) {
        this.localValue = newVal
      }
    },

    computed: {
      hasValue() {
        return this.day && this.month && this.year
      },

      currentValue: {
        get() {
          if (this.yearOnly) {
            return this.localValue
          }
          return this.localValue.split('-')
        },

        set(newValue) {
          this.localValue = this.yearOnly ? newValue : newValue.join('-')

          if(this.localValue === '--' || '') {
            this.$emit('input', '')
          } else {
            this.$emit('input', this.localValue)
          }
        }
      },

      day: {
        get() {
          if (!this.yearOnly) {
            return this.currentValue[DAY_OFFSET]  
          }
          return false
        },

        set(newDay) {
          let value = this.currentValue
          this.currentValue = [value[YEAR_OFFSET], value[MONTH_OFFSET], newDay]
        }
      },

      month: {
        get() {
          return this.currentValue[MONTH_OFFSET]
        },

        set(newMonth) {
          let value = this.currentValue
          this.currentValue = [value[YEAR_OFFSET], newMonth, value[DAY_OFFSET]]
        }
      },

      year: {
        get() {
          if (this.yearOnly) {
            return this.currentValue  
          }
          return this.currentValue[YEAR_OFFSET]
        },

        set(newYear) {
          let value = this.currentValue
          this.currentValue = this.yearOnly ? newYear : [newYear, value[MONTH_OFFSET], value[DAY_OFFSET]]
        }
      },

      daysInMonth() {
        if(this.year && this.month) {
          return new Date(this.year, this.month, FIRST_DAY).getDate()
        } else {
          return MAX_DAYS
        }
      },

      availableDays() {
        return new Array(this.daysInMonth)
          .fill()
          .map((_d, i) => padDidgets(i + DAY_MODIFIER, PAD_LENGTH))
      },

      availableMonths() {
        return new Array(TOTAL_MONTHS)
          .fill()
          .map((_d, i) => padDidgets(i + DAY_MODIFIER, PAD_LENGTH))
      },

      yearFrom() {
        return this.currentYear + this.yearFromOffset + DAY_MODIFIER
      },

      yearTo() {
        return this.currentYear + this.yearToOffset + DAY_MODIFIER
      },

      availableYears() {
        let from = this.yearFrom
        let to = this.yearTo

        return new Array(to - from)
          .fill()
          .map((_d, i) => '' + (i + from))
      },
    }
  }
</script>

<style lang="sass">
  @import '../../../styles/base/variables'

  .date-picker
    .pickdate-lable
      font-size: 12px

    .input-icon
      width: 20px
      height: 20px
      fill: $color-input-icon
      margin: 28px 10px 0px 0px

      .input-label
        font-size: 10px

    .selects
      display: flex
      justify-content: space-between
      width: 100%

      .styled-select
        margin-top: 18px

        .asterisk
          font-size: 12px

        .arrow
          top: 5px

        .input-label
          transform: translateY(10px) scale(1)
          top: 0

        &.has-value
          .input-label
            opacity: 0

        select
          height: 26px

        .input-wrapper
          padding-top: 5px

      .divider
        flex: 1
        margin: 30px 0px 0px 15px

        &::after
          content: '/'

      .small
        flex: 2

      .big
        flex: 3
</style>



// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/inputs/DatePicker.vue